import { CustomerSearchRequestQuery, CustomerSearchResponse } from '@lio/api-types';
import { useCustomers } from '@services/customer';
import { TabOptions, Tabs } from '@components/tabs/Tabs';
import { TabPanel } from '@components/tabs/tabPanel';
import { Layout } from '@layout/Layout';
import { PagePadding } from '@layout/Layout.constants';
import { businessTypeItems } from '@models/customer';
import { GridLink } from '@components/gridLink/GridLink';
import { OverflowTooltip } from '@components/tooltip';
import { TableGridBaseColDef, TableGridRenderCellParams } from '@components/table/types/TableColumnDef.type';
import { formatAddress } from '@utils/address';
import { useMemo, useState } from 'react';
import { ThemeProvider, Box } from '@mui/material';
import composeTheme from '@theme/theme';
import { TableWithStore } from '@components/table/StoreTable/TableWithStore';
import * as styles from '@components/dataGrid/FullPageDataGrid.styles';
import {
  CustomerFieldColumn,
  CustomerFieldLabels,
  MatchStateQuickFilter,
} from '@pages/Customers/CustomersPage.constants';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { MatchStateChip } from '@components/matchState';
import { QuickFiltersProps } from '@components/table/QuickFilters';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { ClockFormat } from '@lio/rule-engine';
import { getDateFormatter, LongDateFormatWithoutSeconds } from '@utils/date';
import { format } from 'date-fns-tz';

type TCustomersTable = {
  items: CustomerSearchResponse['items'];
  params: CustomerSearchRequestQuery;
};

const getCustomerColumns = (isInternalUser: boolean | undefined, clockFormat: ClockFormat): TableGridBaseColDef[] => [
  {
    field: CustomerFieldColumn.Name,
    headerName: CustomerFieldLabels.Name,
    flex: 0.5,
    sortable: true,
    renderCell: (params: TableGridRenderCellParams) => {
      const { id, name } = params.row;
      return (
        <OverflowTooltip tooltipText={name || '-'}>
          <GridLink to={`/customer/${id}`}>{name}</GridLink>
        </OverflowTooltip>
      );
    },
  },
  {
    field: CustomerFieldColumn.BusinessType,
    headerName: CustomerFieldLabels.BusinessType,
    width: 160,
    sortable: false,
    valueGetter: (_, row) => {
      return businessTypeItems.find((el) => el.value === row.businessType)?.label ?? '-';
    },
  },
  {
    field: CustomerFieldColumn.BusinessAddress,
    headerName: CustomerFieldLabels.BusinessAddress,
    flex: 0.5,
    sortable: false,
    renderCell: (params: TableGridRenderCellParams) => {
      const address = params.row.businessAddress;
      return address ? (
        <OverflowTooltip tooltipText={formatAddress(address)}>{formatAddress(address)}</OverflowTooltip>
      ) : (
        '-'
      );
    },
  },
  {
    field: CustomerFieldColumn.Location,
    headerName: CustomerFieldLabels.Location,
    flex: 0.5,
    sortable: false,
    valueGetter: (_, row) => `${row.city ?? '-'}, ${row.state ?? '-'}`,
  },
  {
    field: CustomerFieldColumn.IsBlocked,
    headerName: CustomerFieldLabels.IsBlocked,
    flex: 0.5,
    sortable: true,
    renderCell: (params: TableGridRenderCellParams) => {
      const { isBlocked, blockedByReferencePolicy } = params.row;

      if (!isBlocked) return 'No';

      const policyNumber = blockedByReferencePolicy?.policyNumber?.policyNumber;
      if (blockedByReferencePolicy && policyNumber) {
        const { id: policyId, policyNumberPrefix } = blockedByReferencePolicy;
        return (
          <OverflowTooltip tooltipText={`Blocked by policy ${policyNumber}`}>
            <GridLink
              to={`/policy/${policyId}`}
            >{`Yes - ${getFormattedPolicyNumber(policyNumberPrefix, policyNumber) || '-'}`}</GridLink>
          </OverflowTooltip>
        );
      }

      return 'Yes';
    },
  },
  {
    field: CustomerFieldColumn.CreatedAt,
    headerName: CustomerFieldLabels.CreatedAt,
    flex: 0.5,
    sortable: true,
    renderCell: (params: TableGridRenderCellParams) => (
      <OverflowTooltip>
        {format(new Date(params.row.createdAt), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds))}
      </OverflowTooltip>
    ),
  },
  {
    field: CustomerFieldColumn.UpdatedAt,
    headerName: CustomerFieldLabels.UpdatedAt,
    flex: 0.5,
    sortable: true,
    renderCell: (params: TableGridRenderCellParams) => (
      <OverflowTooltip>
        {format(new Date(params.row.updatedAt), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds))}
      </OverflowTooltip>
    ),
  },
  ...(isInternalUser
    ? [
        {
          field: CustomerFieldColumn.MatchState,
          headerName: CustomerFieldLabels.MatchState,
          flex: 1,
          sortable: false,
          renderCell: (params: TableGridRenderCellParams) => {
            const { matchState, replacedByCustomerId, replacedByCustomer } = params.row;
            if (!matchState) return '-';
            return (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <MatchStateChip matchState={matchState} />
                {matchState === 'MergedAway' && replacedByCustomerId && (
                  <OverflowTooltip tooltipText={`Merged into ${replacedByCustomer?.name ?? 'survivor customer'}`}>
                    <GridLink to={`/customer/${replacedByCustomerId}`}>
                      {replacedByCustomer?.name ? `→ ${replacedByCustomer.name}` : 'View survivor'}
                    </GridLink>
                  </OverflowTooltip>
                )}
              </Box>
            );
          },
        },
      ]
    : []),
];

const customerQuickFilterGroup: QuickFiltersProps['quickFilterGroup'] = [
  {
    queryKey: MatchStateQuickFilter.QueryKey,
    quickFilters: [
      { label: 'All', value: MatchStateQuickFilter.All, defaultChecked: true },
      { label: 'Needs Review', value: MatchStateQuickFilter.NeedsReview },
    ],
  },
];

const FilterCustomerQueryParam: Record<string, string> = {
  [CustomerFieldColumn.Name]: `name`,
  [MatchStateQuickFilter.QueryKey]: MatchStateQuickFilter.QueryKey,
};

const CustomersTab = {
  AllCustomers: 1,
};

export const CustomersPage = () => {
  const [tabValue, setTabValue] = useState<number>(CustomersTab.AllCustomers);
  const [userAuth, layoutPreferences] = useStore((state) => [state.userAuth, state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;
  const columns = useMemo(
    () => getCustomerColumns(userAuth.isInternalUser, clockFormat),
    [userAuth.isInternalUser, clockFormat],
  );

  const tabs: TabOptions[] = [
    {
      value: CustomersTab.AllCustomers,
      label: 'All Customers',
    },
  ];

  const handleChange = (event: any, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Layout
      title='Customers'
      config={{
        padding: PagePadding.TABLE_LIST_PAGE,
      }}
    >
      <Tabs tabOptions={tabs} value={tabValue} onChange={handleChange} />
      <TabPanel selectedTab={tabValue} value={CustomersTab.AllCustomers}>
        <ThemeProvider theme={composeTheme('light')}>
          {tabValue === CustomersTab.AllCustomers && (
            <Box sx={styles.dataGridContainer}>
              <TableWithStore<TCustomersTable>
                tableId='customers-table-store-v2'
                storeProps={{
                  columnSelectorConfig: null,
                  quickFilterGroup: customerQuickFilterGroup,
                }}
                tableProps={{
                  ariaLabel: 'customers-grid',
                  onLoadData: useCustomers,
                  columns,
                  filterConfig: {
                    filterQueryParam: {
                      searchText: 'name',
                      ...FilterCustomerQueryParam,
                    },
                    defaultFilters: { name: '' },
                  },
                  sortConfig: {
                    sortQueryParam: {
                      [CustomerFieldColumn.Name]: (order: string) => `${order}name`,
                      [CustomerFieldColumn.IsBlocked]: (order: string) => `${order}isBlocked`,
                      [CustomerFieldColumn.CreatedAt]: (order: string) => `${order}createdAt`,
                      [CustomerFieldColumn.UpdatedAt]: (order: string) => `${order}updatedAt`,
                    },
                    defaultSort: { field: CustomerFieldColumn.Name, sort: 'desc' },
                  },
                  sxProps: { dataGrid: styles.dataGrid },
                  paginationConfig: { currentPage: 0, rowsPerPage: 25, pageOffset: 0 },
                  noResultsConfig: { noResultsMessage: 'There are currently no customers to display' },
                }}
              />
            </Box>
          )}
        </ThemeProvider>
      </TabPanel>
    </Layout>
  );
};

export default CustomersPage;
